import React, { useState, useEffect } from 'react';
import { 
  DocumentMagnifyingGlassIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

// R2-AUTH: /api/v1/legal/discovery/* now requires a Bearer token — attach it
// the same way the authed legal API client (services/api.js) does.
const discoveryAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
};

export default function DiscoveryResults({ caseId, jobId }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filters, setFilters] = useState({
    tag: '',
    minRelevance: 0,
    privilegeStatus: ''
  });

  useEffect(() => {
    loadResults();
    
    // Poll for updates every 10 seconds while processing
    const interval = setInterval(() => {
      loadResults();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [caseId, filters]);

  const loadResults = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.tag) params.append('filter_tag', filters.tag);
      if (filters.minRelevance > 0) params.append('min_relevance', filters.minRelevance);
      if (filters.privilegeStatus) params.append('privilege_status', filters.privilegeStatus);
      
      const response = await fetch(`/api/v1/legal/discovery/${caseId}/results?${params}`, { headers: discoveryAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setResults(data);
      } else {
        throw new Error('Failed to load results');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generatePrivilegeLog = async () => {
    try {
      const privilegedDocs = results?.documents?.filter(doc => 
        doc.privilege_status !== 'not_privileged'
      ) || [];
      
      const response = await fetch(`/api/v1/legal/discovery/${caseId}/privilege-log`, {
        method: 'POST',
        headers: discoveryAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          documents: privilegedDocs.map(doc => doc.content || doc.title),
          output_format: 'csv'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Download the CSV
        const blob = new Blob([data.content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `privilege_log_${caseId.slice(0, 8)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to generate privilege log:', err);
    }
  };

  const exportProduction = async () => {
    try {
      const response = await fetch(`/api/v1/legal/discovery/${caseId}/export`, {
        method: 'POST',
        headers: discoveryAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          format: 'native',
          include_metadata: true,
          include_privilege_log: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle download or show download link
        window.open(data.download_url, '_blank');
      }
    } catch (err) {
      console.error('Failed to export production:', err);
    }
  };

  if (loading && !results) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading discovery results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-400 mr-2" />
          <div>
            <h3 className="text-red-800 font-medium">Error Loading Results</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <DocumentMagnifyingGlassIcon className="w-8 h-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{results?.total_documents || 0}</p>
              <p className="text-sm text-gray-600">Total Documents</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{results?.stats?.total_responsive || 0}</p>
              <p className="text-sm text-gray-600">Responsive</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ShieldCheckIcon className="w-8 h-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{results?.stats?.total_privileged || 0}</p>
              <p className="text-sm text-gray-600">Privileged</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <DocumentArrowDownIcon className="w-8 h-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{results?.stats?.total_produced || 0}</p>
              <p className="text-sm text-gray-600">For Production</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Discovery Actions</h3>
          <div className="flex items-center space-x-4">
            <button
              onClick={generatePrivilegeLog}
              className="flex items-center px-4 py-2 bg-yellow-600 text-white text-sm rounded-md hover:bg-yellow-700 transition-colors"
            >
              <ShieldCheckIcon className="w-4 h-4 mr-2" />
              Generate Privilege Log
            </button>
            
            <button
              onClick={exportProduction}
              className="flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export Production Set
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Results</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Tag
            </label>
            <select
              value={filters.tag}
              onChange={(e) => setFilters({ ...filters, tag: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Tags</option>
              {/* Would populate from actual tags */}
              <option value="Medical Records">Medical Records</option>
              <option value="Communications">Communications</option>
              <option value="Financial">Financial</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum Relevance
            </label>
            <select
              value={filters.minRelevance}
              onChange={(e) => setFilters({ ...filters, minRelevance: parseFloat(e.target.value) })}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value={0}>Any Relevance</option>
              <option value={0.5}>50% or Higher</option>
              <option value={0.7}>70% or Higher</option>
              <option value={0.9}>90% or Higher</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Privilege Status
            </label>
            <select
              value={filters.privilegeStatus}
              onChange={(e) => setFilters({ ...filters, privilegeStatus: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Documents</option>
              <option value="not_privileged">Not Privileged</option>
              <option value="attorney_client">Attorney-Client</option>
              <option value="work_product">Work Product</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Document Results ({(results?.documents || []).length})
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200">
          {(results?.documents || []).map((doc, index) => (
            <div key={doc.doc_id || index} className="p-6 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-gray-900">{doc.title}</h4>
                    
                    <div className="flex items-center space-x-2">
                      {doc.tags?.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <div className="flex items-center">
                      <ChartBarIcon className="w-4 h-4 mr-1" />
                      Relevance: {Math.round((doc.relevance_score || 0) * 100)}%
                    </div>
                    
                    <div className="flex items-center">
                      <ShieldCheckIcon className="w-4 h-4 mr-1" />
                      {doc.privilege_status === 'not_privileged' ? 'Not Privileged' : 
                       doc.privilege_status === 'attorney_client' ? 'Attorney-Client' :
                       doc.privilege_status === 'work_product' ? 'Work Product' : 
                       doc.privilege_status}
                    </div>
                    
                    <div className="flex items-center">
                      <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                      {doc.production_status === 'produce' ? 'Produce' : 
                       doc.production_status === 'withhold' ? 'Withhold' : 
                       doc.production_status}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => setSelectedDocument(doc)}
                  className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 transition-colors"
                >
                  <EyeIcon className="w-4 h-4 mr-1" />
                  View
                </button>
              </div>
            </div>
          ))}
          
          {(results?.documents || []).length === 0 && (
            <div className="p-12 text-center">
              <DocumentMagnifyingGlassIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No documents found matching current filters.</p>
            </div>
          )}
        </div>
      </div>

      {/* Document viewer modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Document: {selectedDocument.title}
              </h3>
              <button
                onClick={() => setSelectedDocument(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-md">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-800">
                    {Math.round((selectedDocument.relevance_score || 0) * 100)}%
                  </div>
                  <div className="text-xs text-gray-600">Relevance</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-900">
                    {selectedDocument.privilege_status || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-600">Privilege</div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-gray-900">
                    {selectedDocument.production_status || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-600">Status</div>
                </div>
              </div>
              
              {selectedDocument.content && (
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedDocument.content}
                  </pre>
                </div>
              )}
              
              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedDocument(null)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}